import type { MappingProgressState } from '../context/NotificationContext';
import type { ProgressState } from '../types/progress';

type SessionMeta = Record<string, unknown> | null | undefined;

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export type MappingRunStatusInput = {
  batchStatus?: string;
  sessionMeta?: SessionMeta;
  progress?: ProgressState;
  mappingProgress?: MappingProgressState | { type?: string } | null;
  agentBusy?: boolean;
};

function engineModeOverriddenByAgent(sessionMeta?: SessionMeta): boolean {
  if (sessionMeta?.mapping_mode !== 'engine') return false;
  // map_to_schema populates mapped_tables; select_target_schema resets mapping_complete.
  if (asStringArray(sessionMeta.mapped_tables).length > 0) return true;
  return (
    sessionMeta?.intent === 'map_to_target' &&
    sessionMeta.mapping_complete === false
  );
}

/** V2 Mapping editor tab — explicit engine dialog path only. */
export function usesMappingEditorTab(sessionMeta?: SessionMeta): boolean {
  if (sessionMeta?.mapping_mode !== 'engine') return false;
  return !engineModeOverriddenByAgent(sessionMeta);
}

/** Whether the operator has kicked off a map-to-target run (engine or iterative). */
export function hasMappingRunStarted(sessionMeta?: SessionMeta): boolean {
  if (!sessionMeta) return false;
  if (sessionMeta.mapping_complete === true) return true;
  return (
    sessionMeta.intent === 'map_to_target' &&
    Boolean(sessionMeta.target_schema_id)
  );
}

function mappingIntentActive(sessionMeta?: SessionMeta): boolean {
  const meta = sessionMeta ?? {};
  return (
    meta.intent === 'map_to_target' &&
    Boolean(meta.target_schema_id) &&
    meta.mapping_complete !== true
  );
}

function hasIterativeTablesRemaining(sessionMeta?: SessionMeta): boolean {
  const meta = sessionMeta ?? {};
  const targetTables = asStringArray(meta.target_tables);
  const mappedTables = asStringArray(meta.mapped_tables);
  if (targetTables.length === 0) return false;
  return !targetTables.every((t) => mappedTables.includes(t));
}

function progressMappingPhaseActive(progress?: ProgressState): boolean {
  if (!progress?.phases?.length) return false;
  const mappingPhase = progress.phases.find((p) => p.id === 'mapping');
  if (!mappingPhase) return false;
  if (mappingPhase.state === 'running') return true;
  return mappingPhase.tasks.some((t) => t.state === 'running');
}

/**
 * Mapping work happening right now — use to disable toolbar actions
 * (Generate Mapping, Add Transform, etc.).
 *
 * Engine mode: batch.status = 'mapping' for the whole coordinator run.
 * Iterative mode: only while the agent/SSE progress channel is actively
 * working — idle gaps between tables stay interactive.
 */
export function isMappingRunActive(opts: MappingRunStatusInput): boolean {
  const { batchStatus, sessionMeta, progress, mappingProgress, agentBusy } = opts;
  const isIterative = sessionMeta?.mapping_mode === 'iterative';

  if (!isIterative && batchStatus === 'mapping') return true;

  if (
    mappingProgress?.type === 'mapping_start' ||
    mappingProgress?.type === 'mapping_table_progress'
  ) {
    return true;
  }

  if (progressMappingPhaseActive(progress)) return true;

  if (mappingIntentActive(sessionMeta) && agentBusy) return true;

  return false;
}

/**
 * Mapping lifecycle not finished yet — use for overview labels and canvas
 * lifecycle (avoid premature "Mapping Ready"), including idle partial
 * iterative runs where batch.status may stay on 'mapping'.
 */
export function isMappingRunIncomplete(opts: MappingRunStatusInput): boolean {
  const { batchStatus, sessionMeta } = opts;

  if (batchStatus === 'mapping') return true;

  const meta = sessionMeta ?? {};
  if (
    meta.mapping_mode === 'iterative' &&
    mappingIntentActive(sessionMeta) &&
    hasIterativeTablesRemaining(sessionMeta)
  ) {
    return true;
  }

  if (
    batchStatus === 'mapped' &&
    meta.mapping_mode === 'iterative' &&
    meta.mapping_complete !== true
  ) {
    return true;
  }

  return isMappingRunActive(opts);
}

/** @deprecated Prefer isMappingRunActive — kept for callers that mean "active". */
export function isMappingRunInProgress(opts: MappingRunStatusInput): boolean {
  return isMappingRunActive(opts);
}

/** Batch status for mapping lifecycle UI on the pipeline canvas. */
export function effectiveBatchStatusForMapping(
  batchStatus: string | undefined,
  mappingRunIncomplete: boolean,
): string | undefined {
  if (mappingRunIncomplete) return 'mapping';
  return batchStatus;
}

/** Overview / pipeline cards — never show 'Mapping Ready' while incomplete. */
export function effectiveOverviewBatchStatus(
  batchStatus: string | undefined,
  sessionMeta?: SessionMeta,
  mappingRunIncomplete?: boolean,
): string {
  const incomplete =
    mappingRunIncomplete ??
    isMappingRunIncomplete({ batchStatus, sessionMeta });
  if (incomplete) return 'mapping';
  return batchStatus ?? '';
}

/** Whether a completed Generate Mapping run exists for this batch/session. */
export function hasGeneratedMapping(opts: {
  batchStatus?: string;
  sessionMeta?: SessionMeta;
  batchMappingCount?: number;
  mappingTargetTables?: string[];
}): boolean {
  const { batchStatus, sessionMeta, batchMappingCount, mappingTargetTables } = opts;
  if (batchStatus && ['mapped', 'approved'].includes(batchStatus)) return true;
  if (typeof batchMappingCount === 'number' && batchMappingCount > 0) return true;
  if (mappingTargetTables && mappingTargetTables.length > 0) return true;
  const mappedTables = asStringArray(sessionMeta?.mapped_tables);
  if (mappedTables.length > 0) return true;
  return false;
}

/**
 * Whether Delete Mapping should be enabled — only after the canvas shows
 * a completed mapping (e.g. "42 mappings generated"), not while generation
 * is still running or only queued via workspace_meta.mapping_complete.
 */
export function canDeleteGeneratedMapping(
  opts: MappingRunStatusInput & { batchMappingCount?: number; mappingTargetTables?: string[] },
): boolean {
  if (isMappingRunActive(opts) || isMappingRunIncomplete(opts)) {
    return false;
  }
  return hasGeneratedMapping(opts);
}
